import { Button, Center, Checkbox, Spinner, Stack, Text, useToast } from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useEffect, useState } from 'react';
import { testIds } from './testIds';

interface ManageUnitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  placementId: string;
  courseEnrolmentId: number;
}

export function ManageUnitsModal({
  isOpen,
  onClose,
  placementId,
  courseEnrolmentId,
}: ManageUnitsModalProps): React.JSX.Element {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();

  const {
    data: units,
    isLoading,
    isError,
  } = trpcAdmin.workPlacements.placements.unitOptions.useQuery(
    { placementId },
    { enabled: isOpen },
  );

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (isOpen && units != null) {
      setChecked(Object.fromEntries(units.map((u) => [u.unitOfCompetencyId, u.isOnThisPlacement])));
    }
  }, [isOpen, units]);

  const setUnits = trpcAdmin.workPlacements.placements.setUnits.useMutation();

  const checkedCount = Object.values(checked).filter(Boolean).length;

  const toAdd = (units ?? []).filter((u) => checked[u.unitOfCompetencyId] && !u.isOnThisPlacement);
  const toRemove = (units ?? []).filter(
    (u) => !checked[u.unitOfCompetencyId] && u.isOnThisPlacement,
  );
  const hasChanges = toAdd.length > 0 || toRemove.length > 0;
  const isSaving = setUnits.isPending;

  const invalidateQueries = () => {
    void utils.workPlacements.placements.unitOptions.invalidate({ placementId });
    void utils.workPlacements.placements.journeyByEnrolment.invalidate({ courseEnrolmentId });
  };

  const handleSave = async () => {
    try {
      await setUnits.mutateAsync({
        employerPlacementId: placementId,
        addUnitIds: toAdd.map((u) => u.unitOfCompetencyId),
        removeUnitIds: toRemove.map((u) => u.unitOfCompetencyId),
      });
      toast({ status: 'success', title: t`Units updated` });
      invalidateQueries();
      onClose();
    } catch (error) {
      toast({
        status: 'error',
        title: t`Failed to update units`,
        description: error instanceof Error ? error.message : t`Unknown error`,
      });
    }
  };

  return (
    <FormModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      modalContentHeight="auto"
      modalContent={
        <FormMainContainer>
          <FormContentContainer>
            <Text fontSize="lg" fontWeight="semibold" w="full">
              <Trans>Units covered by this placement</Trans>
            </Text>
            <Text fontSize="sm" color="fg.muted" w="full">
              <Trans>
                Tick the units this placement should cover, then save. A placement must cover at
                least one unit, and completed units can't be changed.
              </Trans>
            </Text>

            {isLoading ? (
              <Center py={6} w="full">
                <Spinner size="md" />
              </Center>
            ) : isError ? (
              <Text fontSize="sm" color="fg.error" w="full">
                <Trans>Couldn't load the units for this placement. Please try again.</Trans>
              </Text>
            ) : (units ?? []).length === 0 ? (
              <Text fontSize="sm" color="fg.muted" w="full">
                <Trans>No work-placement units are available for this enrolment.</Trans>
              </Text>
            ) : (
              <Stack gap={3} w="full">
                {(units ?? []).map((unit) => {
                  const isChecked = checked[unit.unitOfCompetencyId] ?? false;
                  const isLastChecked = isChecked && checkedCount <= 1;

                  return (
                    <Checkbox
                      key={unit.unitOfCompetencyId}
                      alignItems="flex-start"
                      data-testid={testIds.unitCheckbox}
                      isChecked={isChecked}
                      isDisabled={isSaving || isLastChecked || unit.isComplete}
                      onChange={(e) =>
                        setChecked((prev) => ({
                          ...prev,
                          [unit.unitOfCompetencyId]: e.target.checked,
                        }))
                      }
                    >
                      <Stack gap={0}>
                        <Text fontSize="sm" fontWeight="medium">
                          {unit.code}
                        </Text>
                        <Text fontSize="xs" color="fg.muted">
                          {unit.title}
                        </Text>
                      </Stack>
                    </Checkbox>
                  );
                })}
              </Stack>
            )}
          </FormContentContainer>

          <FormFooterContainer>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isDisabled={!hasChanges}
              isLoading={isSaving}
              data-testid={testIds.saveUnitsButton}
            >
              <Trans>Save</Trans>
            </Button>
            <Button variant="ghost" onClick={onClose} data-testid={testIds.manageUnitsCloseButton}>
              <Trans>Cancel</Trans>
            </Button>
          </FormFooterContainer>
        </FormMainContainer>
      }
    />
  );
}
